/**
 * ILoader Interface Implementation for LoaderPreProcess
 * Contains methods from the ILoader base interface
 * Non-interface methods are in loaderPreProcess_Accessors.ts
 *
 * ILoader Interface Methods (matching C# ILoader):
 * - Config: defaultReplaceTemplateName, defaultSkipTemplateName, defaultReplaceHtml, defaultSkipHtml, defaultSkipJson, maxArrayOperationItems, maxRecursionDepth
 * - DynamicData: getDynamicDataConfig(appSite)
 * - Results: setDynamicResults(dynamicResults)
 * - Inheritance: getAndClearInheritanceOperations()
 * - API Support: getTemplatesApiResponse(...)
 *
 * These methods are attached to LoaderPreProcess.prototype in the main file
 */

import { JsonUtility } from '../../app/jsonUtility.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';
import type { DynamicResults } from '../../model/dynamicResults.js';
import { JsonObject } from '../../app/json/jsonValue.js';
import type { InheritanceOperation } from '../../model/index.js';
import type { NavigationDecision } from '../../model/index.js';
import type { ITransientStore } from '../../registry/iface/iTransientStore.js';

const configAccessorMethods = {
    defaultReplaceTemplateName(this: LoaderPreProcess): string { return this._config!.defaultReplaceTemplateName; },
    defaultSkipTemplateName(this: LoaderPreProcess): string { return this._config!.defaultSkipTemplateName; },
    defaultReplaceHtml(this: LoaderPreProcess): string { return this._config!.defaultReplaceHtml; },
    defaultSkipHtml(this: LoaderPreProcess): string { return this._config!.defaultSkipHtml; },
    defaultSkipJson(this: LoaderPreProcess): string { return this._config!.defaultSkipJson; },
    defaultAppViewPrefix(this: LoaderPreProcess): string { return this._config!.defaultAppViewPrefix; },
    maxArrayOperationItems(this: LoaderPreProcess): number { return this._config!.maxArrayOperationItems || 1; },
    maxRecursionDepth(this: LoaderPreProcess): number { return this._config!.maxRecursionDepth || 7; },
    maxMergeIterations(this: LoaderPreProcess): number { return this._config!.maxMergeIterations; }
};

/**
 * ILoader interface methods to be attached to LoaderPreProcess prototype
 */
export const iloaderMethods = {
    // #region ILoader Interface Implementation

    // #region Helper Methods

    /**
     * Extracts base component name from a component reuse pattern
     * Pattern: CurrentTemplateName_Number_ComponentName (e.g., MainContent_1_ArrayMerge)
     */
    getBaseComponentName(this: LoaderPreProcess, placeholderOrSlotholderName: string, currentTemplateName: string): { baseComponentName: string; isReuseComponent: boolean } {
        const { baseTemplateName, isReusePattern } = CommonUtil.parseComponentReusePattern(
            placeholderOrSlotholderName, currentTemplateName);
        return { baseComponentName: baseTemplateName, isReuseComponent: isReusePattern };
    },

    // #endregion

    // #region Configuration
    // NOTE: Config wrapper getters are defined via configPropertyDescriptors export below
    // and attached using Object.defineProperties in the main loaderPreProcess.ts file
    // #endregion

    // #region JsonData

    // #endregion

    // #region DynamicData

    /**
     * Gets dynamic data configuration for the specified AppSite
     * Delegates to IConfig.getDynamicDataConfig
     */
    getDynamicDataConfig(this: LoaderPreProcess, appSite: string): DynamicConfigMap | null {
        return this._dynamicDataConfigStore[appSite] || null;
    },

    // #endregion

    // #region Dynamic Data/View Results

    /**
     * Sets the DynamicResults instance for the current merge
     * Engine creates a fresh DynamicResults and passes it to the loader
     */
    setDynamicResults(this: LoaderPreProcess, dynamicResults: DynamicResults): void {
        this._dynamicResults = dynamicResults;
    },

    /**
     * Gets dynamic action configuration for the specified AppSite
     * Delegates to IConfig.getDynamicActionConfig
     */
    getDynamicActionConfig(this: LoaderPreProcess, appSite: string): DynamicConfigMap | null {
        return this._dynamicActionConfigStore[appSite] || null;
    },

    getDynamicDataParams(this: LoaderPreProcess, appSite: string, paramFile: string): JsonObject | null {
        const cached = this._dynamicDataParamsStore.tryGetValue(`${appSite}:${paramFile}`);
        return cached.found && cached.value.isObject ? cached.value.getObject() : null;
    },

    // #region DynamicClientData

    getDynamicClientDataConfig(this: LoaderPreProcess, appSite: string): DynamicConfigMap | null {
        return this._dynamicClientDataConfigStore[appSite] || null;
    },

    getDynamicClientDataParams(this: LoaderPreProcess, appSite: string, paramFile: string): JsonObject | null {
        const cached = this._dynamicClientDataParamsStore.tryGetValue(`${appSite}:${paramFile}`);
        return cached.found && cached.value.isObject ? cached.value.getObject() : null;
    },

    // #endregion

    getDynamicViewConfig(this: LoaderPreProcess, appSite: string): DynamicConfigMap | null {
        return this._dynamicViewConfigStore[appSite] || null;
    },

    getDynamicViewParams(this: LoaderPreProcess, appSite: string, paramFile: string): JsonObject | null {
        const cached = this._dynamicViewParamsStore.tryGetValue(`${appSite}:${paramFile}`);
        return cached.found && cached.value.isObject ? cached.value.getObject() : null;
    },

    getDynamicActionParams(this: LoaderPreProcess, appSite: string, paramFile: string): JsonObject | null {
        const cached = this._dynamicActionParamsStore.tryGetValue(`${appSite}:${paramFile}`);
        return cached.found && cached.value.isObject ? cached.value.getObject() : null;
    },

    getDynamicAppSiteConfig(this: LoaderPreProcess, appSite: string): DynamicConfigMap | null {
        return this._dynamicAppSiteConfigStore[appSite] || null;
    },

    getDynamicAppSiteParams(this: LoaderPreProcess, appSite: string, paramFile: string): JsonObject | null {
        const cached = this._dynamicAppSiteParamsStore.tryGetValue(`${appSite}:${paramFile}`);
        return cached.found && cached.value.isObject ? cached.value.getObject() : null;
    },

    getGlobalData(this: LoaderPreProcess, appSite: string): JsonObject | null {
        return this._globalDataStore[appSite] || null;
    },

    getGlobalViewData(this: LoaderPreProcess, appSite: string): JsonObject | null {
        return this._globalViewDataStore[appSite] || null;
    },

    getGlobalViewDataForComponent(this: LoaderPreProcess, appSite: string, componentName: string): JsonObject | null {
        return this._globalViewDataComponentStore[`${appSite}:${componentName.toLowerCase()}`] || null;
    },

    /**
     * Sets the action mode for DynamicAction handler execution
     */
    setActionMode(this: LoaderPreProcess, actionMode: boolean): void {
        this._actionMode = actionMode;
    },

    // #endregion

    // #region Inheritance

    /**
     * Returns and clears the accumulated inheritance operations
     */
    getAndClearInheritanceOperations(this: LoaderPreProcess): InheritanceOperation[] {
        const ops = this._inheritanceOperations;
        this._inheritanceOperations = [];
        return ops;
    },

    getAndClearNavigationTrace(this: LoaderPreProcess): NavigationDecision[] {
        const trace = this._lastNavigationTrace;
        this._lastNavigationTrace = [];
        return trace;
    },

    // #endregion

    // #region AppFile Resolution

    /**
     * Resolves the AppFile and validates the AppView for a given AppSite.
     * Delegates to IConfig.getAppFile().
     */
    getAppFile(this: LoaderPreProcess, appSite: string, appView: string): { appFile: string; validatedAppView: string } {
        return this._config!.getAppFile(appSite, appView);
    },

    // #endregion

    // #region API Support

    /**
     * PUBLIC - Builds the exact JSON response for /api/get-templates endpoint
     * This is the SINGLE SOURCE OF TRUTH for PreProcess engine API response format
     * Must match C#/Rust/Go/PHP implementations exactly
     */
    getTemplatesApiResponse(this: LoaderPreProcess, appSite: string, appView: string, contextJson: JsonObject, serverTimeMs: number, includeSearchAppSites: boolean = true, maskHtml: boolean = false, orderedTemplateNames: string[] | null = null): string {
        const appFileResult = this._config!.getAppFile(appSite, appView);
        const appFile = appFileResult.appFile;

        const templatesDict = this._getAllTemplatesJson(includeSearchAppSites, orderedTemplateNames);
        const config = this._config!;

        const sb: string[] = [];
        sb.push('{"PreProcessTemplates":{');

        let first = true;
        for (const key in templatesDict) {
            if (!first) sb.push(',');
            first = false;

            sb.push('"');
            sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(key));
            sb.push('":');
            sb.push(templatesDict[key].toJson());
        }

        sb.push('}');
        sb.push(',"AppSite":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(appSite));
        sb.push('","AppFile":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(appFile));
        sb.push('","AppView":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(appView));
        sb.push('","SearchAppSites":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(this.getSearchAppSitesForAppSite(appSite)));
        sb.push('","Config":{');
        sb.push('"defaultReplaceHtml":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(config.defaultReplaceHtml));
        sb.push('","defaultSkipHtml":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(config.defaultSkipHtml));
        sb.push('","defaultSkipJson":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(config.defaultSkipJson));
        sb.push('","defaultReplaceTemplateName":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(config.defaultReplaceTemplateName));
        sb.push('","defaultSkipTemplateName":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(config.defaultSkipTemplateName));
        sb.push('","defaultAppViewPrefix":"');
        sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(config.defaultAppViewPrefix));
        sb.push('","maxRecursionDepth":');
        sb.push(config.maxRecursionDepth.toString());
        sb.push(',"maxArrayOperationItems":');
        sb.push(config.maxArrayOperationItems.toString());
        sb.push(',"dynamicDataUseResolvedAppSite":');
        sb.push(config.dynamicDataUseResolvedAppSite ? 'true' : 'false');
        sb.push('}');
        sb.push(',"ContextJson":');
        sb.push(JsonUtility.serializeObject(contextJson));

        this._serializePerSiteStores(sb, appSite);

        sb.push(',"ServerTimeMs":');
        sb.push(serverTimeMs.toString());
        sb.push('}');

        return sb.join('');
    },

    setTransientStore(this: LoaderPreProcess, transientStore: ITransientStore): void {
        this._transientStore = transientStore;
    },

    getTransientStore(this: LoaderPreProcess): ITransientStore {
        return this._transientStore;
    },

    _serializePerSiteStores(this: LoaderPreProcess, sb: string[], appSite: string): void {
        const allSites = [appSite];
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !allSites.includes(trimmed)) allSites.push(trimmed);
            }
        }
        // Also cover any other loaded AppSite (e.g. a dynamic-AppSite redirect target that Templates
        // carries) so its dynamic stores ship too; otherwise the client re-resolves to that AppSite but
        // lacks its DynamicData/View config and renders a partial result. Sorted for byte-stable output.
        const extraSites: string[] = [];
        for (const site of this._store.getAllAppSites().keys()) {
            if (!allSites.includes(site)) extraSites.push(site);
        }
        extraSites.sort((a, b) => { const la = a.toLowerCase(), lb = b.toLowerCase(); return la < lb ? -1 : la > lb ? 1 : 0; });
        for (const site of extraSites) allSites.push(site);
        sb.push(',"PerSiteStores":{');

        sb.push('"GlobalData":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(allSites[i])); sb.push('":');
            const gd = this.getGlobalData(allSites[i]);
            sb.push(gd ? JsonUtility.serializeObject(gd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewData":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(allSites[i])); sb.push('":');
            const gvd = this.getGlobalViewData(allSites[i]);
            if (gvd) {
                const resolved = new JsonObject();
                for (const componentName of gvd.keys()) {
                    const resolvedData = this.getGlobalViewDataForComponent(allSites[i], componentName);
                    if (resolvedData) resolved.set(componentName, resolvedData);
                }
                sb.push(resolved.size > 0 ? JsonUtility.serializeObject(resolved) : 'null');
            } else {
                sb.push('null');
            }
        }
        sb.push('},');

        // The client re-merge ships the config of every handler tier that EXECUTES on the client: DynamicView,
        // and the PURE client bindings below. Server-only Data/Action/AppSite handlers — and the Embedded
        // client-data PREPARER — stay unshipped; their results are already baked into the shipped templates
        // or arrive as the delivered island.
        this._serializePerSiteDynamicConfig(sb, allSites, 'DynamicViewConfig', (site: string) => this.getDynamicViewConfig(site));
        sb.push(',');
        this._serializePerSiteParamsFromStore(sb, allSites, 'DynamicViewParams', this._dynamicViewParamsStore);
        sb.push(',');
        // PURE client bindings + the params they name. A pure handler resolves its params from the BINDING
        // (tryResolvePureClientHandlerParams), so without these the reconstruction sources silently skip it
        // and the replay diverges from the server render.
        this._serializePerSiteDynamicConfig(sb, allSites, 'DynamicClientConfig', (site: string) => CommonUtil.filterPureClientBindings(this.getDynamicClientDataConfig(site)));
        sb.push(',');
        this._serializePerSiteParamsFromStore(sb, allSites, 'DynamicClientParams',
            CommonUtil.filterPureClientParams(this._dynamicClientDataParamsStore, allSites, (site: string) => this.getDynamicClientDataConfig(site)));

        sb.push('}');
    },

    _serializeDynamicConfigToJson(this: LoaderPreProcess, config: DynamicConfigMap): string {
        return DynamicConfigMap.serializeDynamicConfigToJson(config);
    },

    _serializePerSiteDynamicConfig(this: LoaderPreProcess, sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null): void {
        sb.push('"'); sb.push(key); sb.push('":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(allSites[i])); sb.push('":');
            const config = getter(allSites[i]);
            sb.push(config ? DynamicConfigMap.serializeDynamicConfigToJson(config) : 'null');
        }
        sb.push('}');
    },

    _serializePerSiteParamsFromStore(this: LoaderPreProcess, sb: string[], allSites: string[], key: string, paramsStore: JsonObject): void {
        sb.push('"'); sb.push(key); sb.push('":{');
        let firstSite = true;
        for (let i = 0; i < allSites.length; i++) {
            const site = allSites[i];
            const prefix = `${site}:`;
            const siteParams: Record<string, JsonObject> = {};
            for (const storeKey of paramsStore.keys()) {
                if (storeKey.toLowerCase().startsWith(prefix.toLowerCase())) {
                    const storeValue = paramsStore.get(storeKey);
                    if (storeValue.isObject) {
                        siteParams[storeKey.substring(prefix.length)] = storeValue.getObject();
                    }
                }
            }
            if (Object.keys(siteParams).length === 0) continue;
            if (!firstSite) sb.push(',');
            firstSite = false;
            sb.push('"'); sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(site)); sb.push('":{');
            let firstParam = true;
            for (const paramKey of Object.keys(siteParams)) {
                if (!firstParam) sb.push(',');
                firstParam = false;
                sb.push('"'); sb.push((this.constructor as typeof LoaderPreProcess)._escapeJsonString(paramKey)); sb.push('":');
                sb.push(JsonUtility.serializeObject(siteParams[paramKey]));
            }
            sb.push('}');
        }
        sb.push('}');
    },

    // #endregion

    // #region Component ID

    /**
     * Gets the ComponentID for a component from DynamicView configuration
     * Looks up the BaseView handler's param file to find the ComponentID
     * Falls back to camelCase of componentName if not found
     */
    getComponentId(this: LoaderPreProcess, appSite: string, componentName: string): string | null {
        let dynamicConfig = this.getDynamicViewConfig(appSite);
        let configAppSite: string | null = dynamicConfig ? appSite : null;

        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicViewConfig(trimmed);
                    if (dynamicConfig) {
                        configAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (!dynamicConfig) return null;

        let matchedKey: string | null = null;
        const componentNameLower = componentName.toLowerCase();
        for (const key of dynamicConfig.keys()) {
            if (key !== '*' && key.toLowerCase() === componentNameLower) {
                matchedKey = key;
                break;
            }
        }
        if (matchedKey === null && dynamicConfig.has('*')) {
            matchedKey = '*';
        }

        if (matchedKey === null) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (!searchConfig) continue;

                    for (const key of searchConfig.keys()) {
                        if (key !== '*' && key.toLowerCase() === componentNameLower) {
                            matchedKey = key;
                            dynamicConfig = searchConfig;
                            configAppSite = trimmed;
                            break;
                        }
                    }
                    if (matchedKey !== null) break;

                    if (searchConfig.has('*')) {
                        matchedKey = '*';
                        dynamicConfig = searchConfig;
                        configAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (matchedKey === null) {
            const configsToSearch: Array<{ config: DynamicConfigMap; appSite: string }> = [];
            configsToSearch.push({ config: dynamicConfig, appSite: configAppSite || '' });

            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (searchConfig && !configsToSearch.some(entry => entry.appSite === trimmed)) {
                        configsToSearch.push({ config: searchConfig, appSite: trimmed });
                    }
                }
            }

            for (const entry of configsToSearch) {
                for (const [, handlers] of entry.config) {
                    for (const handler of handlers) {
                        if (handler.iType.toLowerCase() !== 'baseview' || !handler.iParamFile) continue;
                        const paramJson = this.getDynamicViewParams(entry.appSite, handler.iParamFile);
                        if (!paramJson || !paramJson.has('ComponentID')) continue;
                        const componentIdValue = paramJson.get('ComponentID');
                        const componentId = componentIdValue.isString
                            ? componentIdValue.asString()
                            : componentIdValue.toString();
                        if (componentId?.toLowerCase() === componentNameLower) return componentId;
                    }
                }
            }
            return null;
        }

        const handlersValue = dynamicConfig.get(matchedKey);
        if (!Array.isArray(handlersValue)) return null;

        let baseViewParamFile: string | null = null;
        for (const handlerObj of handlersValue) {
            if (!handlerObj || typeof handlerObj !== 'object') continue;
            const iViewType = handlerObj.iType;
            if (!iViewType) continue;

            if (iViewType.toLowerCase() === 'baseview') {
                if (handlerObj.iParamFile) {
                    baseViewParamFile = handlerObj.iParamFile;
                }
                break;
            }
        }

        if (baseViewParamFile === null) return null;

        const paramJson = this.getDynamicViewParams(configAppSite!, baseViewParamFile);
        if (paramJson) {
            const componentIdValue = paramJson.get('ComponentID');
            if (componentIdValue && componentIdValue.isString) {
                const componentId = componentIdValue.asString()!;
                if (componentId) return componentId;
            }
        }

        if (!componentName) return componentName;
        return componentName.charAt(0).toLowerCase() + componentName.substring(1);
    }

    // #endregion

    // #endregion
};

/**
 * Static methods for ILoader interface (none currently)
 */
export const iloaderStaticMethods = {};

/**
 * Config property descriptors for ILoader interface
 * These are attached to the prototype using Object.defineProperties
 * to maintain proper getter semantics while keeping definitions in this file
 */
export const configPropertyDescriptors: PropertyDescriptorMap = {
    DefaultReplaceTemplateName: {
        get: configAccessorMethods.defaultReplaceTemplateName,
        configurable: true
    },
    defaultReplaceTemplateName: {
        get: configAccessorMethods.defaultReplaceTemplateName,
        configurable: true
    },
    DefaultSkipTemplateName: {
        get: configAccessorMethods.defaultSkipTemplateName,
        configurable: true
    },
    defaultSkipTemplateName: {
        get: configAccessorMethods.defaultSkipTemplateName,
        configurable: true
    },
    DefaultReplaceHtml: {
        get: configAccessorMethods.defaultReplaceHtml,
        configurable: true
    },
    defaultReplaceHtml: {
        get: configAccessorMethods.defaultReplaceHtml,
        configurable: true
    },
    DefaultSkipHtml: {
        get: configAccessorMethods.defaultSkipHtml,
        configurable: true
    },
    defaultSkipHtml: {
        get: configAccessorMethods.defaultSkipHtml,
        configurable: true
    },
    DefaultSkipJson: {
        get: configAccessorMethods.defaultSkipJson,
        configurable: true
    },
    defaultSkipJson: {
        get: configAccessorMethods.defaultSkipJson,
        configurable: true
    },
    DefaultAppViewPrefix: {
        get: configAccessorMethods.defaultAppViewPrefix,
        configurable: true
    },
    defaultAppViewPrefix: {
        get: configAccessorMethods.defaultAppViewPrefix,
        configurable: true
    },
    MaxArrayOperationItems: {
        get: configAccessorMethods.maxArrayOperationItems,
        configurable: true
    },
    maxArrayOperationItems: {
        get: configAccessorMethods.maxArrayOperationItems,
        configurable: true
    },
    MaxRecursionDepth: {
        get: configAccessorMethods.maxRecursionDepth,
        configurable: true
    },
    maxRecursionDepth: {
        get: configAccessorMethods.maxRecursionDepth,
        configurable: true
    },
    maxMergeIterations: {
        get: configAccessorMethods.maxMergeIterations,
        configurable: true
    }
};
